import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Buildings,
  UploadSimple,
  Trash,
  Plus,
  DotsThreeVertical,
  PencilSimple,
  Star,
  FloppyDisk,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TimeField } from "@/components/DateTimeField";
import { hoursLines } from "@/lib/openingHours";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { refreshCompany } from "@/lib/company";

const Section = ({ title, sub, children, testid }) => (
  <div
    data-testid={testid}
    className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
  >
    <h2 className="text-base font-bold">{title}</h2>
    {sub && <p className="mt-0.5 text-xs text-zinc-500">{sub}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
      {label}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-zinc-600">{hint}</p>}
  </div>
);

const blankContact = () => ({
  id: "",
  title: "",
  contact_type: "Payables — vendor",
  phones: [""],
  email: "",
  address: "",
  is_default: false,
});

export default function CompanySettings() {
  const nav = useNavigate();
  const [conf, setConf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [logo, setLogo] = useState(null);
  const logoInput = useRef(null);
  const signInput = useRef(null);

  const load = useCallback(async () => {
    const [{ data }, branding] = await Promise.all([
      api.get("/settings/company"),
      api.get("/settings/branding").catch(() => ({ data: {} })),
    ]);
    setConf(data);
    setLogo(branding.data?.logo || null);
    refreshCompany(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) => setConf((c) => ({ ...c, [key]: value }));
  const setHours = (key, value) =>
    setConf((c) => ({ ...c, hours: { ...c.hours, [key]: value } }));

  const setDay = (index, patch) =>
    setConf((c) => ({
      ...c,
      hours: {
        ...c.hours,
        days: c.hours.days.map((d, n) =>
          n === index ? { ...d, ...patch } : d,
        ),
      },
    }));

  const setRange = (dayIndex, rangeIndex, patch) =>
    setConf((c) => ({
      ...c,
      hours: {
        ...c.hours,
        days: c.hours.days.map((d, n) =>
          n === dayIndex
            ? {
                ...d,
                ranges: d.ranges.map((r, i) =>
                  i === rangeIndex ? { ...r, ...patch } : r,
                ),
              }
            : d,
        ),
      },
    }));

  const copyMondayToWeek = () =>
    setConf((c) => {
      const monday = c.hours.days[0];
      return {
        ...c,
        hours: {
          ...c.hours,
          days: c.hours.days.map((d, n) =>
            n >= 1 && n <= 4
              ? {
                  ...d,
                  closed: monday.closed,
                  ranges: monday.ranges.map((r) => ({ ...r })),
                }
              : d,
          ),
        },
      };
    });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/company", {
        name: conf.name || "",
        website_label: conf.website_label || "",
        website_url: conf.website_url || "",
        default_cc: conf.default_cc || "",
        address: conf.address || "",
        phone: conf.phone || "",
        sms_phone: conf.sms_phone || "",
        email: conf.email || "",
        representative: conf.representative || "",
        hours: conf.hours,
        currency: conf.currency,
        number_format: conf.number_format,
        measurement: conf.measurement,
        contacts: conf.contacts || [],
      });
      setConf(data);
      refreshCompany(data);
      toast.success("Company details saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/settings/logo", body);
      setLogo(data.logo);
      toast.success("Logo updated");
    } catch {
      toast.error("Could not upload that logo");
    }
    e.target.value = "";
  };

  const uploadSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/settings/company/signature", body);
      setConf(data);
      toast.success("Signature updated");
    } catch {
      toast.error("Could not upload that signature");
    }
    e.target.value = "";
  };

  const saveContact = () => {
    const list = [...(conf.contacts || [])];
    const idx = list.findIndex((c) => c.id && c.id === editing.id);
    const row = { ...editing, phones: editing.phones.filter(Boolean) };
    if (idx >= 0) list[idx] = row;
    else list.push({ ...row, id: `new-${Date.now()}` });
    set("contacts", list);
    setEditing(null);
    toast.info("Press Save changes to keep this contact");
  };

  const contacts = conf?.contacts || [];
  const previewHours = hoursLines(conf?.hours?.days);

  if (!conf) {
    return (
      <div className="grain min-h-screen">
        <AppHeader title="My Company" />
        <main className="relative z-10 pt-6">
          <p className="text-sm text-zinc-500">Loading company details…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen">
      <AppHeader title="My Company" />
      <main className="relative z-10 pb-32 pt-6">
        <button
          onClick={() => nav("/settings")}
          data-testid="company-back"
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </button>

        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-indigo-400/25 bg-indigo-500/10 text-indigo-300">
              <Buildings size={20} weight="bold" />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                My Company
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                These details print on your quotes, invoices and work orders.
              </p>
            </div>
          </div>
          <div className="mt-5 border-t border-zinc-800 pt-4">
            <Button
              data-testid="company-save-button"
              disabled={busy}
              onClick={save}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <FloppyDisk size={15} weight="bold" className="mr-2" />
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <Section
            testid="company-info-section"
            title="Company information"
            sub="Shown at the top of every document you send"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Company name">
                <Input
                  data-testid="company-name-input"
                  value={conf.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Phone for calls">
                <Input
                  data-testid="company-phone-input"
                  value={conf.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Phone for texting">
                <Input
                  data-testid="company-sms-phone-input"
                  value={conf.sms_phone || ""}
                  onChange={(e) => set("sms_phone", e.target.value)}
                  placeholder="Same as calls"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
                <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-600">
                  Usually your Twilio number. Adds a “Text us” link on the
                  website — leave blank to text the call number.
                </p>
              </Field>
              <Field label="Website (display name)">
                <Input
                  data-testid="company-website-label-input"
                  value={conf.website_label}
                  onChange={(e) => set("website_label", e.target.value)}
                  placeholder="brionigranite.com"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Website (link)">
                <Input
                  data-testid="company-website-url-input"
                  value={conf.website_url}
                  onChange={(e) => set("website_url", e.target.value)}
                  placeholder="https://brionigranite.com"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Office email">
                <Input
                  data-testid="company-email-input"
                  value={conf.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field
                label="Default CC on documents"
                hint="Quietly copied on every quote and invoice you email"
              >
                <Input
                  data-testid="company-cc-input"
                  value={conf.default_cc}
                  onChange={(e) => set("default_cc", e.target.value)}
                  placeholder="office@brionigranite.com"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Address">
                  <Textarea
                    data-testid="company-address-input"
                    value={conf.address}
                    onChange={(e) => set("address", e.target.value)}
                    rows={2}
                    className="border-zinc-800 bg-zinc-950"
                  />
                </Field>
              </div>
            </div>
          </Section>

          <Section
            testid="company-brand-section"
            title="Logo & signature"
            sub="Printed on quotes and invoices, signature above the representative's name"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Logo
                </div>
                <div className="mt-2 flex h-28 items-center justify-center rounded-lg border border-dashed border-zinc-700 bg-zinc-950 p-3">
                  {logo ? (
                    <img
                      src={`${API}/public/logo`}
                      alt="Logo"
                      data-testid="company-logo-preview"
                      className="max-h-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-zinc-600">No logo yet</span>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <Button
                    data-testid="company-logo-upload"
                    variant="outline"
                    onClick={() => logoInput.current?.click()}
                    className="h-11 flex-1 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                  >
                    <UploadSimple size={14} weight="bold" className="mr-2" />
                    Upload
                  </Button>
                  {logo && (
                    <Button
                      data-testid="company-logo-remove"
                      variant="outline"
                      onClick={async () => {
                        await api.delete("/settings/logo");
                        setLogo(null);
                        toast.success("Logo removed");
                      }}
                      className="h-11 border-red-500/30 bg-zinc-950 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash size={14} weight="bold" />
                    </Button>
                  )}
                </div>
                <input
                  ref={logoInput}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadLogo}
                  data-testid="company-logo-input"
                />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Signature
                </div>
                <div className="mt-2 flex h-28 items-center justify-center rounded-lg border border-dashed border-zinc-700 bg-white p-3">
                  {conf.has_signature ? (
                    <img
                      src={`${API}/public/company-signature`}
                      alt="Signature"
                      data-testid="company-signature-preview"
                      className="max-h-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-zinc-500">
                      No signature yet
                    </span>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <Button
                    data-testid="company-signature-upload"
                    variant="outline"
                    onClick={() => signInput.current?.click()}
                    className="h-11 flex-1 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                  >
                    <UploadSimple size={14} weight="bold" className="mr-2" />
                    Upload
                  </Button>
                  {conf.has_signature && (
                    <Button
                      data-testid="company-signature-remove"
                      variant="outline"
                      onClick={async () => {
                        const { data } = await api.delete(
                          "/settings/company/signature",
                        );
                        setConf(data);
                        toast.success("Signature removed");
                      }}
                      className="h-11 border-red-500/30 bg-zinc-950 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash size={14} weight="bold" />
                    </Button>
                  )}
                </div>
                <input
                  ref={signInput}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadSignature}
                  data-testid="company-signature-input"
                />
                <div className="mt-3">
                  <Field label="Representative">
                    <Input
                      data-testid="company-representative-input"
                      value={conf.representative}
                      onChange={(e) => set("representative", e.target.value)}
                      placeholder="Nijaz Mustedanagic"
                      className="h-12 border-zinc-800 bg-zinc-950"
                    />
                  </Field>
                </div>
              </div>
            </div>
          </Section>

          <Section
            testid="company-hours-section"
            title="Opening hours"
            sub="Shown on your website and used for the times offered when you schedule a template, fabrication or install"
          >
            <div className="space-y-2" data-testid="company-hours-days">
              {(conf.hours.days || []).map((row, dayIndex) => (
                <div
                  key={row.day}
                  data-testid={`hours-row-${row.day}`}
                  className="rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="w-24 shrink-0 text-sm font-bold">
                      {conf.options.day_labels?.[row.day] || row.day}
                    </span>
                    <button
                      type="button"
                      data-testid={`hours-closed-${row.day}`}
                      onClick={() =>
                        setDay(dayIndex, {
                          closed: !row.closed,
                          ranges:
                            row.closed && !row.ranges.length
                              ? [{ open: "08:00", close: "17:00" }]
                              : row.ranges,
                        })
                      }
                      className={`h-8 rounded-full px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                        row.closed
                          ? "bg-zinc-800 text-zinc-400"
                          : "bg-emerald-500/15 text-emerald-300"
                      }`}
                    >
                      {row.closed ? "Closed" : "Open"}
                    </button>
                    {!row.closed && (
                      <button
                        type="button"
                        data-testid={`hours-add-${row.day}`}
                        disabled={row.ranges.length >= 3}
                        onClick={() =>
                          setDay(dayIndex, {
                            ranges: [
                              ...row.ranges,
                              { open: "13:00", close: "17:00" },
                            ],
                          })
                        }
                        className="ml-auto flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:text-indigo-200 disabled:opacity-30"
                      >
                        <Plus size={12} weight="bold" /> Add hours
                      </button>
                    )}
                  </div>
                  {!row.closed && (
                    <div className="mt-3 space-y-2">
                      {row.ranges.map((r, rangeIndex) => (
                        <div
                          key={rangeIndex}
                          className="flex items-center gap-2"
                        >
                          <div className="min-w-0 flex-1">
                            <TimeField
                              value={r.open}
                              onChange={(v) =>
                                setRange(dayIndex, rangeIndex, { open: v })
                              }
                              testId={`hours-open-${row.day}-${rangeIndex}`}
                            />
                          </div>
                          <span className="text-xs text-zinc-600">to</span>
                          <div className="min-w-0 flex-1">
                            <TimeField
                              value={r.close}
                              onChange={(v) =>
                                setRange(dayIndex, rangeIndex, { close: v })
                              }
                              testId={`hours-close-${row.day}-${rangeIndex}`}
                            />
                          </div>
                          <button
                            type="button"
                            data-testid={`hours-remove-${row.day}-${rangeIndex}`}
                            onClick={() =>
                              setDay(dayIndex, {
                                ranges: row.ranges.filter(
                                  (_, n) => n !== rangeIndex,
                                ),
                                closed: row.ranges.length === 1,
                              })
                            }
                            aria-label="Remove these hours"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400"
                          >
                            <Trash size={13} weight="bold" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-end gap-4">
              <Field label="Time zone">
                <Select
                  value={conf.hours.timezone}
                  onValueChange={(v) => setHours("timezone", v)}
                >
                  <SelectTrigger
                    data-testid="company-timezone-select"
                    className="h-12 w-56 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    {conf.options.timezones.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz.replace("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <button
                type="button"
                data-testid="hours-copy-monday"
                onClick={copyMondayToWeek}
                className="h-12 rounded-lg border border-zinc-800 px-4 text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-100"
              >
                Copy Monday to Tue–Fri
              </button>
            </div>

            <div
              className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
              data-testid="hours-preview"
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                How it reads on your website
              </div>
              <div className="mt-2 space-y-0.5 text-sm text-zinc-300">
                {previewHours.length ? (
                  previewHours.map((line) => <div key={line}>{line}</div>)
                ) : (
                  <span className="text-zinc-600">
                    No open days set — nothing will show.
                  </span>
                )}
              </div>
            </div>
          </Section>

          <Section
            testid="company-format-section"
            title="Currency & formats"
            sub="How money and measurements read across the app"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Currency">
                <Select
                  value={conf.currency}
                  onValueChange={(v) => set("currency", v)}
                >
                  <SelectTrigger
                    data-testid="company-currency-select"
                    className="h-12 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    {conf.options.currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Number format">
                <Select
                  value={conf.number_format}
                  onValueChange={(v) => set("number_format", v)}
                >
                  <SelectTrigger
                    data-testid="company-number-format-select"
                    className="h-12 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    {conf.options.number_formats.map((n) => (
                      <SelectItem key={n.code} value={n.code}>
                        {n.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label="Measurement"
                hint="Metric drawings and labels are not converted yet"
              >
                <Select
                  value={conf.measurement}
                  onValueChange={(v) => set("measurement", v)}
                >
                  <SelectTrigger
                    data-testid="company-measurement-select"
                    className="h-12 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    <SelectItem value="imperial">Imperial (in, ft²)</SelectItem>
                    <SelectItem value="metric">Metric (mm, m²)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>
        </div>

        <Section
          testid="company-contacts-section"
          title="Contacts"
          sub="Bill to, remit to and warehouse details used on documents"
        >
          <div className="overflow-hidden rounded-lg border border-zinc-800">
            <div className="hidden grid-cols-[1.2fr_1fr_1.6fr_auto] gap-4 border-b border-zinc-800 bg-zinc-950 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 sm:grid">
              <span>Title</span>
              <span>Contact type</span>
              <span>Contact info</span>
              <span className="w-8" />
            </div>

            {contacts.length === 0 && (
              <p
                className="px-4 py-6 text-sm text-zinc-500"
                data-testid="company-no-contacts"
              >
                No contacts yet.
              </p>
            )}

            {contacts.map((c) => (
              <div
                key={c.id || c.title}
                data-testid={`company-contact-row-${c.id || c.title}`}
                className="grid grid-cols-[1fr_auto] items-start gap-3 border-b border-zinc-800/70 px-4 py-3 last:border-0 sm:grid-cols-[1.2fr_1fr_1.6fr_auto] sm:gap-4"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">
                    {c.title || "—"}
                  </div>
                  {c.is_default && (
                    <span className="mt-1 inline-block rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-300">
                      Default
                    </span>
                  )}
                </div>
                <div className="hidden text-xs text-zinc-400 sm:block">
                  {c.contact_type}
                </div>
                <div className="hidden min-w-0 text-xs text-zinc-400 sm:block">
                  {(c.phones || []).map((p) => (
                    <div key={p} className="font-mono-data truncate">
                      {p}
                    </div>
                  ))}
                  {c.email && <div className="truncate">{c.email}</div>}
                  {c.address && (
                    <div className="truncate text-zinc-500">{c.address}</div>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid={`company-contact-menu-${c.id || c.title}`}
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 shrink-0 text-zinc-400 hover:bg-zinc-800"
                    >
                      <DotsThreeVertical size={20} weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-48 border-zinc-800 bg-zinc-900"
                  >
                    <DropdownMenuItem
                      data-testid="company-contact-edit"
                      onClick={() =>
                        setEditing({
                          ...c,
                          phones: c.phones?.length ? c.phones : [""],
                        })
                      }
                      className="text-xs font-semibold uppercase tracking-widest"
                    >
                      <PencilSimple size={15} weight="bold" className="mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="company-contact-default"
                      onClick={() =>
                        set(
                          "contacts",
                          contacts.map((row) =>
                            row.contact_type === c.contact_type
                              ? { ...row, is_default: row.id === c.id }
                              : row,
                          ),
                        )
                      }
                      className="text-xs font-semibold uppercase tracking-widest"
                    >
                      <Star size={15} weight="bold" className="mr-2" />
                      Make default
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="company-contact-delete"
                      onClick={() =>
                        set(
                          "contacts",
                          contacts.filter((row) => row.id !== c.id),
                        )
                      }
                      className="text-xs font-semibold uppercase tracking-widest text-red-400"
                    >
                      <Trash size={15} weight="bold" className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}

            <div className="border-t border-zinc-800 bg-zinc-950 px-4 py-3">
              <Button
                data-testid="company-add-contact"
                variant="ghost"
                onClick={() => setEditing(blankContact())}
                className="h-11 text-xs font-bold uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/10"
              >
                <Plus size={16} weight="bold" className="mr-2" /> Add Contact
              </Button>
            </div>
          </div>
        </Section>
      </main>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent
          className="border-zinc-800 bg-zinc-900"
          data-testid="company-contact-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base">
              {editing?.id ? "Edit contact" : "Add contact"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Title">
                <Input
                  data-testid="contact-title-input"
                  autoFocus
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                  placeholder="Accounts payable"
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Contact type">
                <Select
                  value={editing.contact_type}
                  onValueChange={(v) =>
                    setEditing({ ...editing, contact_type: v })
                  }
                >
                  <SelectTrigger
                    data-testid="contact-type-select"
                    className="h-11 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    {conf.options.contact_types.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Phones">
                <div className="space-y-2">
                  {editing.phones.map((p, i) => (
                    <Input
                      key={i}
                      data-testid={`contact-phone-input-${i}`}
                      value={p}
                      onChange={(e) => {
                        const phones = [...editing.phones];
                        phones[i] = e.target.value;
                        setEditing({ ...editing, phones });
                      }}
                      placeholder="(555) 123-4567"
                      className="h-11 border-zinc-800 bg-zinc-950"
                    />
                  ))}
                  {editing.phones.length < 3 && (
                    <Button
                      data-testid="contact-add-phone"
                      variant="ghost"
                      onClick={() =>
                        setEditing({
                          ...editing,
                          phones: [...editing.phones, ""],
                        })
                      }
                      className="h-9 text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/10"
                    >
                      <Plus size={14} weight="bold" className="mr-2" /> Add
                      another phone
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Email">
                <Input
                  data-testid="contact-email-input"
                  value={editing.email}
                  onChange={(e) =>
                    setEditing({ ...editing, email: e.target.value })
                  }
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Address">
                <Textarea
                  data-testid="contact-address-input"
                  value={editing.address}
                  onChange={(e) =>
                    setEditing({ ...editing, address: e.target.value })
                  }
                  rows={2}
                  className="border-zinc-800 bg-zinc-950"
                />
              </Field>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400">
                <input
                  data-testid="contact-default-checkbox"
                  type="checkbox"
                  checked={editing.is_default}
                  onChange={(e) =>
                    setEditing({ ...editing, is_default: e.target.checked })
                  }
                  className="h-4 w-4 accent-indigo-500"
                />
                Default for this contact type
              </label>
              <Button
                data-testid="contact-save-button"
                onClick={saveContact}
                className="h-11 w-full bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {editing.id ? "Update contact" : "Add contact"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
